import React, { useState, useMemo, useCallback, useEffect } from 'react'
import update from 'immutability-helper'
import { Modal as SModal, Image as SImage, Input as SInput, Dropdown as SDropdown, DropdownItemProps as SDropdownItemProps, Form as SForm, Message as SMessage } from 'semantic-ui-react'
import trade from './trade.png'
import PromiseButton from '../../../../components/PromiseButton'
import { Resource } from '@prometheus/types'
import { translateResource, translateResourceColorRaw } from '../../../../utils/translateResource'
import { useSelector } from '../../../../hooks/useSelector'
import firebase from 'firebase/app'
import usePlayerActions from '../../hooks/usePlayerActions'
import { round } from 'lodash'
import { truncateCash } from '@prometheus/utils'
import { Button, Card, CardBody, CardFooter, CardHeader, Dropdown, DropdownItem, Input, Modal, Stack, Typography } from '@prometheus/components'
import { evt } from '@/utils/evt'
import PromiseButtonV2 from '@/components/PromiseButtonV2'

const db = firebase.firestore()

interface ResourceTradeButtonProps {
  open: boolean
  setOpen: Function
  resource: keyof Resource
}

interface ResourceTradeButtonState {
  quantity: number
  price: number
  user?: string
}

export const ResourceTradeButton = ({ open, setOpen, resource }: ResourceTradeButtonProps) => {
  const players = useSelector(s => s.game.metadata?.players) || {}
  const resource_costs = useSelector(s => s.game.metadata?.resource_costs) || {}
  const uid = useSelector(s => s.auth.uid)
  const resources = useSelector(s => s.game.sheet?.resources) || { [resource]: 0 }
  const { onOfferTrade } = usePlayerActions()

  const [s, setState] = useState<ResourceTradeButtonState>({
    quantity: 1,
    price: round((resource_costs[resource]?.cash || 0), 2),
  })

  const items = useMemo((): DropdownItem[] => {
    return Object.entries(players).filter(([id]) => id !== uid).map(([id, player]) => ({
      label: player.name,
      value: id,
    }))
  }, [players, uid])

  const onChange = useCallback((e) => {
    let { name, value } = e.target
    setState(state => update(state, {
      [name]: { $set: value },
    }))
  }, [])

  const onSubmit = useCallback(async () => {
    if (!s.user || !uid) {
      throw new Error('Failed precondition.')
    }

    await onOfferTrade('offer', {
      id: db.collection('games').doc().id,
      from: uid,
      to: s.user,
      cost: { cash: (s.price) * s.quantity },
      price: Number(s.price),
      quantity: Number(s.quantity),
      resource,
    })

    setOpen(false)
  }, [uid, onOfferTrade, s, resource, setOpen])

  const cashCost = resource_costs[resource]?.cash || 0
  useEffect(() => {
    if (open) {
      setState(s => ({
        quantity: 1,
        price: round((cashCost), 2),
        user: s.user,
      }))
    }
  }, [open, cashCost, resource])

  let error = ''
  if (s.quantity > resources[resource]) {
    error = `You do not have enough ${translateResource(resource)} for this offer.`
  } else if (!s.user) {
    error = 'Please select a player.'
  }

  if (!open) return null;

  return (
    <Modal onClose={() => setOpen(false)}>
      <Card padding="sm">
        <CardHeader>
          <Typography style={{ color: translateResourceColorRaw(resource) }}>
            Offer {s.quantity} {translateResource(resource)} for {truncateCash(s.quantity * (s.price), undefined, true)}
          </Typography>
        </CardHeader>
        <CardBody>
          <Stack>
            <Stack direction="row" style={{ flexWrap: 'wrap' }}>
              <Input label="Quantity" type="number" value={s.quantity} name="quantity" onChange={onChange} />
              <Input label="Price (per unit)" type="number" value={s.price} name="price" onChange={onChange} />
              <Dropdown style={{ width: '150px', maxWidth: '100%' }} label="Player" items={items} value={s.user} onChange={(value) => onChange(evt('user', value))} />
            </Stack>

            <Card padding="sm" borderColor="secondary">
              <Typography color="secondary">Total Cost: {truncateCash(s.quantity * s.price)}</Typography>
            </Card>
            {error && (
              <Card padding="sm" borderColor="danger">
                <Typography color="danger">{error}</Typography>
              </Card>
            )}
          </Stack>
        </CardBody>
        <CardFooter>
          <PromiseButtonV2 ignoreGameLogic variant="warning" outline onClick={() => setOpen(false)}>Cancel</PromiseButtonV2>
          <PromiseButtonV2 ignoreGameLogic onClick={onSubmit} disabled={!s.user || !!error} variant="primary">Send Offer!</PromiseButtonV2>
        </CardFooter>
      </Card>
    </Modal>
  )
}
